import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    TextField,
    Paper,
    CircularProgress,
    Alert,
} from "@mui/material";
import { CloudUpload, Image, VideoFile, Description } from "@mui/icons-material";
import axiosInstance from "../../../api/axiosInstance";
import base_url from "../../../constants";
import useNotification from "../../common/Notification";
import { WB_SX } from "../../../ui/ux/uiTokens";

const MediaUploadStep = ({
    selectedTemplate,
    templateDetails,
    loadingTemplateDetails,
    onMediaUploaded,
    onPreviewUrlChange,
}) => {
    const [uploadingMedia, setUploadingMedia] = useState(false);
    const [mediaFile, setMediaFile] = useState(null);
    const [mediaPreviewUrl, setMediaPreviewUrl] = useState("");
    const [headerComponent, setHeaderComponent] = useState(null);
    const notify = useNotification();

    useEffect(() => {
        if (templateDetails && templateDetails.components) {
            const header = templateDetails.components.find(
                (c) => c.type === "HEADER"
            );
            setHeaderComponent(header);
        }
    }, [templateDetails]);

    const handleMediaFileChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            setMediaFile(file);
            // Create object URL for immediate preview in this component
            const objectUrl = URL.createObjectURL(file);
            setMediaPreviewUrl(objectUrl);
            
            // Convert to base64 data URL for persistent preview in other components
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64DataUrl = reader.result;
                if (onPreviewUrlChange) {
                    onPreviewUrlChange(base64DataUrl);
                }
            };
            reader.onerror = () => {
                console.error("Error reading file");
                // Fallback to object URL if base64 conversion fails
                if (onPreviewUrlChange) {
                    onPreviewUrlChange(objectUrl);
                }
            };
            reader.readAsDataURL(file);
        }
    };

    const handleMediaUpload = async () => {
        if (!mediaFile || !headerComponent) {
            notify("Please select a file to upload", "error");
            return;
        }

        setUploadingMedia(true);
        try {
            const formData = new FormData();
            formData.append("file", mediaFile);
            formData.append("type", headerComponent.format.toLowerCase());

            const response = await axiosInstance.post(
                `${base_url}/whatsapp-business/media/upload`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            const mediaId = response.data?.id;
            const cloudflarePath = response.data?.cloudflare_path || null;
            const mediaType = response.data?.type || null;
            
            if (mediaId) {
                // Pass media info as object with id, media_link, and media_type
                if (onMediaUploaded) {
                    onMediaUploaded({
                        id: mediaId,
                        media_link: cloudflarePath,
                        media_type: mediaType
                    });
                }
                // Preview URL should already be set as base64 data URL from handleMediaFileChange
                notify("Media uploaded successfully!", "success");
            } else {
                notify("Failed to get media ID", "error");
            }
        } catch (error) {
            console.error("Error uploading media:", error);
            notify(
                error.response?.data?.error ||
                    error.response?.data?.message ||
                    "Failed to upload media",
                "error"
            );
        } finally {
            setUploadingMedia(false);
        }
    };

    // Cleanup preview URL
    useEffect(() => {
        return () => {
            if (mediaPreviewUrl) {
                URL.revokeObjectURL(mediaPreviewUrl);
            }
        };
    }, [mediaPreviewUrl]);

    if (!selectedTemplate) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="info">
                    Please select a template in Step 1 first.
                </Alert>
            </Box>
        );
    }

    if (loadingTemplateDetails || !templateDetails) {
        return (
            <Box sx={{ p: 2, display: "flex", justifyContent: "center", alignItems: "center" }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ ml: 2 }}>
                    {loadingTemplateDetails ? "Loading template details..." : "Template details not available"}
                </Typography>
            </Box>
        );
    }

    const needsMediaUpload =
        headerComponent &&
        ["IMAGE", "VIDEO", "DOCUMENT"].includes(headerComponent.format);

    return (
        <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box", p: { xs: 1, sm: 2 } }}>
            <Typography variant="h6" sx={{ ...WB_SX.sectionTitle, mb: 2 }}>
                Upload Media (if required)
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
                Parameters will be automatically taken from the selected group in the next step.
                {needsMediaUpload && " Please upload media for the header if required."}
            </Alert>

            {/* Media Upload Section */}
            {needsMediaUpload && (
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Upload {headerComponent.format} for Header
                    </Typography>
                    <Paper
                        sx={{
                            p: 2,
                            border: "2px dashed",
                            borderColor: "primary.main",
                            textAlign: "center",
                        }}
                    >
                        <input
                            accept={
                                headerComponent.format === "IMAGE"
                                    ? "image/*"
                                    : headerComponent.format === "VIDEO"
                                    ? "video/*"
                                    : "application/pdf,.doc,.docx"
                            }
                            style={{ display: "none" }}
                            id="media-upload"
                            type="file"
                            onChange={handleMediaFileChange}
                        />
                        <label htmlFor="media-upload">
                            <Button
                                variant="outlined"
                                component="span"
                                startIcon={<CloudUpload />}
                                disabled={uploadingMedia}
                                sx={WB_SX.secondaryButton}
                            >
                                Choose File
                            </Button>
                        </label>

                        {mediaPreviewUrl && (
                            <Box sx={{ mt: 2 }}>
                                {headerComponent.format === "IMAGE" && (
                                    <Box
                                        component="img"
                                        src={mediaPreviewUrl}
                                        alt="Preview"
                                        sx={{
                                            maxWidth: "100%",
                                            maxHeight: 200,
                                            borderRadius: 1,
                                        }}
                                    />
                                )}
                                {headerComponent.format === "VIDEO" && (
                                    <Box
                                        component="video"
                                        src={mediaPreviewUrl}
                                        controls
                                        sx={{
                                            maxWidth: "100%",
                                            maxHeight: 200,
                                            borderRadius: 1,
                                        }}
                                    />
                                )}
                                {headerComponent.format === "DOCUMENT" && (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            justifyContent: "center",
                                        }}
                                    >
                                        <Description fontSize="large" />
                                        <Typography>
                                            {mediaFile?.name || "Document"}
                                        </Typography>
                                    </Box>
                                )}
                                <Button
                                    variant="contained"
                                    onClick={handleMediaUpload}
                                    disabled={uploadingMedia}
                                    sx={{ ...WB_SX.primaryButton, mt: 2 }}
                                    startIcon={
                                        uploadingMedia ? (
                                            <CircularProgress size={20} />
                                        ) : (
                                            <CloudUpload />
                                        )
                                    }
                                >
                                    {uploadingMedia
                                        ? "Uploading..."
                                        : "Upload Media"}
                                </Button>
                            </Box>
                        )}
                    </Paper>
                </Box>
            )}

            {!needsMediaUpload && (
                <Alert severity="success">
                    This template does not require media upload. You can proceed to the next step.
                </Alert>
            )}
        </Box>
    );
};

export default MediaUploadStep;

